import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

interface QueryGroupByProps {
  value: string[] | undefined;
  editable: boolean;
  rootLabel?: string;
  onChange: (value: string[] | undefined) => void;
}

export function QueryGroupBy({
  value,
  editable,
  rootLabel,
  onChange,
}: QueryGroupByProps) {
  const tooltipContent = 'Group results by one or more fields';
  const [simpleFilters, setSimpleFilters] = useState<
    SimpleFilterDefinition[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!rootLabel) {
      setSimpleFilters(null);
      return;
    }

    const entity = getQueryEntity(rootLabel);
    const getFilters = entity?.getSimpleFilters;
    if (!getFilters) {
      setSimpleFilters(null);
      return;
    }

    setIsLoading(true);
    const loadFilters = async () => {
      try {
        const definitions = await getFilters();
        setSimpleFilters(definitions);
      } catch (error) {
        console.error('Failed to load simple filters for group by:', error);
        setSimpleFilters(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadFilters();
  }, [rootLabel]);

  const options = useMemo(() => {
    if (!simpleFilters || simpleFilters.length === 0) return [];
    return simpleFilters.map(filter => ({
      label: filter.uiField,
      value: filter.apiField,
    }));
  }, [simpleFilters]);

  const selectedFields = value ?? [];

  const availableOptions = useMemo(
    () => options.filter(opt => !selectedFields.includes(opt.value)),
    [options, selectedFields]
  );

  const handleAdd = (field: string) => {
    if (!field) return;
    const next = [...selectedFields, field];
    onChange(next.length > 0 ? next : undefined);
  };

  const handleRemove = (field: string) => {
    const next = selectedFields.filter(f => f !== field);
    onChange(next.length > 0 ? next : undefined);
  };

  const getDisplayLabel = (field: string) => {
    const match = options.find(opt => opt.value === field);
    return match?.label ?? field;
  };

  if (editable) {
    return (
      <div className="flex items-center gap-2">
        <Tooltip title={tooltipContent}>
          <span className="text-chariot-text-secondary text-sm">Group By:</span>
        </Tooltip>
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedFields.map(field => (
            <span
              key={field}
              className="bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-sm"
            >
              {getDisplayLabel(field)}
              <button
                type="button"
                onClick={() => handleRemove(field)}
                className="text-chariot-text-secondary hover:text-chariot-text-primary"
                aria-label={`Remove ${getDisplayLabel(field)}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          {availableOptions.length > 0 && (
            <Select value="" onValueChange={handleAdd} disabled={isLoading}>
              <SelectTrigger
                className="h-7 w-36 text-sm"
                data-testid="query-header-groupby-select"
              >
                <SelectValue
                  placeholder={isLoading ? 'Loading...' : 'Add field'}
                />
              </SelectTrigger>
              <SelectContent>
                {availableOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    );
  }

  if (!selectedFields.length) return null;

  return (
    <div className="flex items-center gap-2">
      <Tooltip title={tooltipContent}>
        <span className="text-chariot-text-secondary text-sm">Group By:</span>
      </Tooltip>
      <span className="text-chariot-text-primary text-sm">
        {selectedFields.map(getDisplayLabel).join(', ')}
      </span>
    </div>
  );
}
